import { NgZone } from "@angular/core";
import type { ModuleRefreshInformation } from "src/AngularJS/Dashboard/Models/module-refresh.models";
import { ModuleManagerRefreshType } from "src/shared/models/module-manager/module-manager-options.model";
import { ClearViewUtils } from "../utils";

export class ModuleRefreshServiceBase {
    refreshTypes: { [key in ModuleManagerRefreshType]: {
        /** time (ms) that the timer refreshes */
        intervalAmt: number,
        /** time (ms) that the timer refreshes when not on the active dashboard */
        backgroundAmt: number
    }};

    /** holds the module queue objects in order of next timer */
    refreshQueue: ModuleRefreshInformation[] = []; 
    /** the module queue objects that can be accessed by module key  */
    refreshQueueByModuleKey: {[moduleKey: string]: ModuleRefreshInformation} = {};
    /** don't allow adding to the queue while processing */
    refreshLocked = false;
    /** used to hold items while queue is locked */
    refreshHoldingQueue: ModuleRefreshInformation[] = [];
    /** holds a reference to the timer so that it can be cancelled */
    refreshTimer: number = null; 
    /** whichever refresh the timer is currently heading towards */
    currentRefreshTarget: number = null; 

    constructor(
        fromSharedService: boolean = false,
        private _ngZone: NgZone = null,
    ) {
        if (fromSharedService)
            return ModuleRefreshServiceBase.getServiceBase();

        // Refresh intervals are in milliseconds:
        this.refreshTypes = {
            auto: null, // required to match enum, but auto does not have a time attached
            default: { intervalAmt: 90_000, backgroundAmt: 180_000 },
            fiveMinute: { intervalAmt: 300_000, backgroundAmt: 600_000 },
            fifteenMinute: { intervalAmt: 60_000 * 15, backgroundAmt: 60_000 * 30 },
            minute: { intervalAmt: 60_000, backgroundAmt: 120_000 },
            cas: { intervalAmt: 30_000, backgroundAmt: 60_000 },
            casShort: { intervalAmt: 15_000, backgroundAmt: 30_000 },
            veryShort: { intervalAmt: 7_000, backgroundAmt: 14_000 },
            long: { intervalAmt: 30 * 60_000, backgroundAmt: 60 * 60_000 },
            hour: { intervalAmt: 60 * 60_000, backgroundAmt: 120 * 60_000 },
            fourHour: { intervalAmt: 4 * 60 * 60_000, backgroundAmt: 8 * 60 * 60_000 },
        };
    }

    static getServiceBase(): ModuleRefreshServiceBase {
        return ClearViewUtils.getSharedService(ModuleRefreshServiceBase, 'ModuleRefreshServiceBase');
    }

    queueRefresh(refreshObj: ModuleRefreshInformation) {
        // remove any refresh timers related to this module
        let moduleKey = refreshObj.moduleKey;
        this.removeRefresh(moduleKey); 
        
        // queue this refresh and update the timer if necessary 
        this.refreshQueueByModuleKey[moduleKey] = refreshObj;
        if (this.refreshLocked) {
            //console.log(`[${refreshObj.moduleKey}] added to holding queue`);
            this.refreshHoldingQueue.push(refreshObj);                
        } else {
            //console.log(`[${refreshObj.moduleKey}] added to queue`);
            this.refreshQueue.push(refreshObj);
        }
        this.startNextRefreshTimer();
    }

    removeRefresh(moduleKey: string) {
        if (this.refreshQueueByModuleKey[moduleKey] !== undefined) {
            let oldRefreshObj = this.refreshQueueByModuleKey[moduleKey];
            let queueIndex = this.refreshQueue.indexOf(oldRefreshObj);
            let lengthBefore = this.refreshQueue.length;
            if (queueIndex >= 0) {
                this.refreshQueue.splice(queueIndex, 1);
                //console.log(`[${oldRefreshobj.moduleKey}] removed by MC (length before: ${lengthBefore}, length after: ${mcFactory.refreshQueue.length})`);
            }
            else // shouldn't happen - console warn if it does so it can be fixed
                console.warn('ModuleContainer: Refresh queue has been desynced');

            delete this.refreshQueueByModuleKey[moduleKey];
        }
    }

    startNextRefreshTimer() {
        // make sure there even is a queue
        if (this.refreshQueue.length <= 0) {
            return;
        }

        // make sure the queue is in order
        //console.log(`SORTING QUEUE - length: ${mcFactory.refreshQueue.length}`);
        this.refreshQueue.sort((a, b) => a.nextRefresh - b.nextRefresh);
        let firstInQueue = this.refreshQueue[0];
        //console.log(`First in queue: ${firstInQueue.moduleKey}`);

        // check if target should change
        if (this.refreshTimer && this.currentRefreshTarget !== firstInQueue.nextRefresh) {
            // cancel current timer, it will get recreated below
            clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
        }

        if (this.refreshTimer != null) {
            // we don't need to change the refresh timer
            return;
        }

        // queue next item
        // If Angular2 run timeout outside ngZone so we don't trigger Change Detection
        if(!!this._ngZone)
            this.refreshTimer = this._ngZone.runOutsideAngular(() => window.setTimeout(() => this.onTimerFinished(), firstInQueue.nextRefresh - Date.now()));
        else
            this.refreshTimer = window.setTimeout(() => this.onTimerFinished(), firstInQueue.nextRefresh - Date.now());
        this.currentRefreshTarget = firstInQueue.nextRefresh;
    }

    lockQueue() {
        this.refreshLocked = true;
    }

    unlockQueue() {
        this.refreshLocked = false;
        // queue any items that were added while locked
        if (this.refreshHoldingQueue.length > 0) {
            //console.log('SOME ITEMS ADDED WHILE LOCKED: ' + mcFactory.refreshHoldingQueue.length);
            this.refreshQueue = this.refreshQueue.concat(this.refreshHoldingQueue);
            this.refreshHoldingQueue = [];
        }
    }

    onTimerFinished() {
        this.lockQueue();
        let completeTime = Date.now();
        let itemsToRemove = [];
        for(let i = 0; i < this.refreshQueue.length; i++) {
            let refreshObj = this.refreshQueue[i];
            if (refreshObj.nextRefresh <= completeTime) {
                if (typeof refreshObj.refresh === 'function') {
                    refreshObj.refresh();
                }

                //console.log(`[${refreshObj.moduleKey}] refreshing`);
                delete this.refreshQueueByModuleKey[refreshObj.moduleKey];
                itemsToRemove.push(refreshObj);
            } else { // because the list is sorted, we can exit now
                break;
            }
        }

        // remove the completed items from the queue
        for(var q in itemsToRemove) {
            var item = itemsToRemove[q];
            var indexInQueue = this.refreshQueue.indexOf(item);
            if (indexInQueue >= 0) {
                //console.log(`[${item.moduleKey}] removed from queue`);
                this.refreshQueue.splice(indexInQueue, 1);
            }
        }
        this.refreshTimer = null;
        this.unlockQueue();

        // queue next item
        this.startNextRefreshTimer();
    }
}
